import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, API, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Download, DollarSign, Clock, ArrowLeft, ArrowUpDown, Pencil, Search, Receipt, Upload, Trash2, FileText } from "lucide-react";
import PodGallery from "@/components/ui/PodGallery";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";
import { formatDate, formatDateTime } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";

// Document categories a customer can upload against a job. These land on the
// job's e_doc_files, so admin (Job Status) and the assigned driver see them too.
const DOC_TYPES = ["Delivery Order", "Outturn Report", "Delivery Docket", "Interim Receipt", "Booking Confirmation", "Other"];

// Freight/item type + service-option presets — keep in sync with the copies
// in CreateJob.jsx and JobStatus.jsx so the edit form here offers the same
// choices a job was created with.
const FREIGHT_TYPES = ["LCL", "Airfreight", "General", "FCL", "Interstate"];
const FREIGHT_OTHER = "Other (Specify)";
const ITEM_TYPES = ["Pallet", "Crate", "Box", "IBC", "Carton", "CHEP"];
const ITEM_OTHER = "Other (Specify)";
const YN = ["Yes", "No", "N/A"];

const STATUS = {
  needs_approval: { c: "#f59e0b", l: "Pending Approval" },
  assigned:       { c: "#19e6c8", l: "Assigned" },
  pending:        { c: "#19e6c8", l: "Assigned" }, // backward compat
  onboard:        { c: "#a78bfa", l: "Onboard" },
  done:           { c: "#22c55e", l: "Delivered" },
  futile:         { c: "#ef4444", l: "Futile" },
};

const ACTIVE_STATUSES = new Set(["needs_approval", "assigned", "pending", "onboard"]);
const HISTORY_STATUSES = new Set(["done", "futile"]);

// Keep in sync with the copies in JobStatus.jsx and DriverRuns.jsx.
const ETA_WINDOWS = [
  { value: "06:00-08:00", label: "6:00 AM – 8:00 AM" },
  { value: "08:00-10:00", label: "8:00 AM – 10:00 AM" },
  { value: "10:00-12:00", label: "10:00 AM – 12:00 PM" },
  { value: "12:00-14:00", label: "12:00 PM – 2:00 PM" },
  { value: "14:00-16:00", label: "2:00 PM – 4:00 PM" },
  { value: "16:00-18:00", label: "4:00 PM – 6:00 PM" },
  { value: "18:00-20:00", label: "6:00 PM – 8:00 PM" },
];
const etaWindowLabel = (value) => ETA_WINDOWS.find((w) => w.value === value)?.label || value;
const fmtDate = (s) => (s ? formatDate(s) : "—");

export default function ClientJobs({ historyMode = false }) {
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [remarksState, setRemarksState] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data);
      const init = {};
      data.forEach((j) => { init[j.id] = { text: j.client_remarks || "", saving: false }; });
      setRemarksState(init);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const saveClientRemarks = async (jobId) => {
    const text = remarksState[jobId]?.text ?? "";
    setRemarksState((s) => ({ ...s, [jobId]: { ...s[jobId], saving: true } }));
    try {
      await api.patch(`/jobs/${jobId}/remarks`, { client_remarks: text });
      toast.success("Remarks saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRemarksState((s) => ({ ...s, [jobId]: { ...s[jobId], saving: false } }));
    }
  };

  const downloadDoc = async (jobId, type, jobRef) => {
    try {
      const token = localStorage.getItem("df_token");
      const path = type === "cn" ? "consignment-note.pdf" : "invoice.pdf";
      const res = await fetch(`${API}/jobs/${jobId}/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "cn" ? `CN-${jobRef}.pdf` : `INV-${jobRef}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
  };

  const visibleJobs = jobs
    .filter((j) => (historyMode ? HISTORY_STATUSES.has(j.status) : ACTIVE_STATUSES.has(j.status)))
    .filter((j) => {
      if (!historyMode) return true;
      if (dateFrom && (j.job_date || "") < dateFrom) return false;
      if (dateTo && (j.job_date || "") > dateTo) return false;
      return true;
    })
    .filter((j) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (j.job_ref || "").toLowerCase().includes(q) ||
        (j.carrier_ref || "").toLowerCase().includes(q) ||
        (j.job_date || "").includes(q)
      );
    })
    .sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sortDir === "desc" ? tb - ta : ta - tb;
    });

  if (active) {
    const job = jobs.find((j) => j.id === active.id) || active;
    return (
      <ClientJobDetailView
        job={job}
        historyMode={historyMode}
        onClose={() => setActive(null)}
        refresh={load}
        remarksState={remarksState}
        setRemarksState={setRemarksState}
        saveClientRemarks={saveClientRemarks}
        downloadDoc={downloadDoc}
      />
    );
  }

  return (
    <div data-testid="client-jobs-page">
      <h1 className="brand-title text-2xl md:text-3xl mb-1">
        {historyMode ? "Job History" : "Job Status"}
      </h1>
      <p className="text-white/60 text-sm mb-5">
        {historyMode
          ? "Your completed & cancelled jobs. View proof of delivery, consignment note and invoice."
          : "Track your active jobs. View proof of delivery, consignment note and invoice."}
      </p>

      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-end gap-3 mb-3">
          <button
            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors"
          >
            <ArrowUpDown size={14} /> {sortDir === "desc" ? "Newest First" : "Oldest First"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              placeholder="Search booking ID, carrier ref, date (YYYY-MM-DD)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="df-input-rect !py-2 !text-sm !pl-9 w-full"
            />
          </div>
          {historyMode && (
            <>
              <span className="text-white/80 font-semibold text-sm">From:</span>
              <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} className="!py-2 !text-sm" data-testid="filter-date-from" />
              <span className="text-white/80 font-semibold text-sm">To:</span>
              <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} className="!py-2 !text-sm" data-testid="filter-date-to" />
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-white/60 hover:text-white text-sm underline">Clear</button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="table-scroll rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[#19e6c8] text-left">
              {["Booking ID", "PU Date", "Del Date", "Status", "Pick-Up / Drop-Off", "Item Details", "ETA", "Estimate (inc GST)"].map((h) => (
                <th key={h} className="py-4 px-5 font-semibold tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleJobs.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-white/50">
                  {historyMode ? "No completed or cancelled jobs yet." : "No active jobs. Create a job from the sidebar."}
                </td>
              </tr>
            )}
            {visibleJobs.map((j) => {
              const m = STATUS[j.status] || STATUS.pending;
              const itemLine = (j.item_qty || j.item_type) ? `${j.item_qty || 0} x ${j.item_type || "item"}` : "";
              return (
                <tr
                  key={j.id}
                  onClick={() => setActive(j)}
                  className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  data-testid={`client-job-${j.id}`}
                >
                  {/* Booking ID — client ref + DRIVN carrier ref, same pairing as the admin table. */}
                  <td className="py-4 px-5 whitespace-nowrap">
                    <div className="text-white font-semibold">{j.job_ref || "—"}</div>
                    {j.carrier_ref && (
                      <div className="text-xs font-mono mt-0.5" style={{ color: "#a78bfa" }}>{j.carrier_ref}</div>
                    )}
                  </td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs font-mono">{fmtDate(j.job_date)}</td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs font-mono">{fmtDate(j.pod_completed_at || j.delivery_arrived_at)}</td>
                  <td className="py-4 px-5">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                      style={{ background: m.c + "22", color: m.c }}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: m.c }} /> {m.l}
                    </span>
                  </td>
                  <td className="py-4 px-5 text-white/75 max-w-[220px]">
                    <div className="truncate" title={j.pickup_address}>PU: {j.pickup_address || "—"}</div>
                    <div className="truncate mt-0.5" title={j.dropoff_address}>DO: {j.dropoff_address || "—"}</div>
                  </td>
                  <td className="py-4 px-5 text-white/75 whitespace-nowrap text-xs">
                    <div>{itemLine || "—"}</div>
                    {(j.volume_cbm || j.weight_kgs) && (
                      <div className="text-white/45 mt-0.5">{j.volume_cbm || 0} cbm · {j.weight_kgs || 0} kgs</div>
                    )}
                  </td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs">
                    {j.eta_date ? (
                      <>
                        <div>{formatDate(j.eta_date)}</div>
                        {j.eta_window && <div className="text-white/45 mt-0.5">{etaWindowLabel(j.eta_window)}</div>}
                      </>
                    ) : "—"}
                  </td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs font-mono">
                    {j.quote_amount != null
                      ? `$${Number(j.quote_amount).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Detail view for a single job — mirrors the admin portal's three-stage flow:
// a read-only summary card is the default view (with an Edit Job button), and
// editing swaps in the same form fields the job was created with. Cancel/Save
// return to the card, not the list. Only the fields already exposed to clients
// elsewhere in this tab are surfaced here — no pricing, driver assignment or
// admin-only controls.
function ClientJobDetailView({ job, historyMode, onClose, refresh, remarksState, setRemarksState, saveClientRemarks, downloadDoc }) {
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [data, setData] = useState({ ...job });
  const [busy, setBusy] = useState(false);
  // "Other" when the stored freight type isn't one of the presets (and is set).
  const [freightSel, setFreightSel] = useState(() =>
    job.freight_type && !FREIGHT_TYPES.includes(job.freight_type) ? FREIGHT_OTHER : (job.freight_type || "")
  );
  // "Other" when the stored item type isn't one of the presets (and is set).
  const [itemSel, setItemSel] = useState(() =>
    job.item_type && !ITEM_TYPES.includes(job.item_type) ? ITEM_OTHER : (job.item_type || "")
  );
  const m = STATUS[job.status] || STATUS.pending;
  const editable = !historyMode && ["needs_approval", "assigned", "pending"].includes(job.status);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Keep the read-only card in sync with the latest job data (e.g. a remarks
  // save elsewhere reloading the list) without clobbering in-progress edits.
  useEffect(() => {
    if (mode === "view") setData({ ...job });
  }, [job, mode]);

  const startEdit = () => {
    setData({ ...job });
    setFreightSel(job.freight_type && !FREIGHT_TYPES.includes(job.freight_type) ? FREIGHT_OTHER : (job.freight_type || ""));
    setItemSel(job.item_type && !ITEM_TYPES.includes(job.item_type) ? ITEM_OTHER : (job.item_type || ""));
    setMode("edit");
  };

  const cancelEdit = () => {
    setData({ ...job });
    setMode("view");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/jobs/${job.id}`, {
        ...data,
        item_qty: Number(data.item_qty) || 0,
        volume_cbm: Number(data.volume_cbm) || 0,
        weight_kgs: Number(data.weight_kgs) || 0,
      });
      toast.success("Job updated");
      await refresh();
      setMode("view");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="client-job-detail-page">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="brand-title text-2xl truncate">{job.job_ref || "—"}</h3>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: m.c + "22", color: m.c }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.c }} /> {m.l}
            </span>
          </div>
          {job.carrier_ref && (
            <p className="text-xs font-mono mt-0.5" style={{ color: "#a78bfa" }}>{job.carrier_ref}</p>
          )}
        </div>
        {mode === "view" && editable && (
          <button
            onClick={startEdit}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors shrink-0"
            style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }}
            data-testid={`client-job-edit-${job.id}`}
          >
            <Pencil size={14} /> Edit Job
          </button>
        )}
      </div>

      <div className="bg-[#15151f] border border-white/10 rounded-2xl p-4 sm:p-7" data-testid="client-job-detail-card">
        {mode === "edit" ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Lbl label="Client Reference / Booking ID">
                <input value={data.job_ref || ""} onChange={(e) => set("job_ref", e.target.value)} className="df-input-rect" data-testid={`client-job-ref-${job.id}`} />
              </Lbl>
              {data.carrier_ref && (
                <Lbl label="Carrier Reference">
                  <input disabled value={data.carrier_ref} className="df-input-rect opacity-70 font-mono" style={{ color: "#a78bfa" }} />
                </Lbl>
              )}
              <Lbl label="Pick-Up Address">
                <AddressAutocomplete
                  value={data.pickup_address || ""}
                  onChange={(v) => set("pickup_address", v)}
                  onResolved={(p) => set("pickup_address", p.formatted || p.line1)}
                  className="df-input-rect"
                />
              </Lbl>
              <Lbl label="Drop-Off Address">
                <AddressAutocomplete
                  value={data.dropoff_address || ""}
                  onChange={(v) => set("dropoff_address", v)}
                  onResolved={(p) => set("dropoff_address", p.formatted || p.line1)}
                  className="df-input-rect"
                />
              </Lbl>
              <Lbl label="Job Date">
                <DatePicker value={data.job_date} onChange={(v) => set("job_date", v)} />
              </Lbl>

              <Lbl label="Freight Type">
                <select
                  value={freightSel}
                  onChange={(e) => {
                    setFreightSel(e.target.value);
                    set("freight_type", e.target.value === FREIGHT_OTHER ? "" : e.target.value);
                  }}
                  className="df-input-rect"
                >
                  <option value="" className="bg-[#15151f]">Select</option>
                  {[...FREIGHT_TYPES, FREIGHT_OTHER].map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
                </select>
                {freightSel === FREIGHT_OTHER && (
                  <input
                    value={data.freight_type || ""}
                    onChange={(e) => set("freight_type", e.target.value)}
                    placeholder="Specify freight type"
                    className="df-input-rect mt-2"
                  />
                )}
              </Lbl>
              <Lbl label="Item Type">
                <select
                  value={itemSel}
                  onChange={(e) => {
                    setItemSel(e.target.value);
                    set("item_type", e.target.value === ITEM_OTHER ? "" : e.target.value);
                  }}
                  className="df-input-rect"
                >
                  <option value="" className="bg-[#15151f]">Select Item</option>
                  {[...ITEM_TYPES, ITEM_OTHER].map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
                </select>
                {itemSel === ITEM_OTHER && (
                  <input
                    value={data.item_type || ""}
                    onChange={(e) => set("item_type", e.target.value)}
                    placeholder="Specify item type"
                    className="df-input-rect mt-2"
                  />
                )}
              </Lbl>
              <Lbl label="Quantity"><input type="number" min="0" value={data.item_qty ?? ""} onChange={(e) => set("item_qty", e.target.value)} className="df-input-rect" /></Lbl>
              <Lbl label="Volume (cbm)"><input type="number" step="0.01" min="0" value={data.volume_cbm ?? ""} onChange={(e) => set("volume_cbm", e.target.value)} className="df-input-rect" /></Lbl>
              <Lbl label="Weight (kgs)"><input type="number" step="0.01" min="0" value={data.weight_kgs ?? ""} onChange={(e) => set("weight_kgs", e.target.value)} className="df-input-rect" /></Lbl>

              <Lbl label="Dimensions"><textarea rows="3" value={data.dimensions || ""} onChange={(e) => set("dimensions", e.target.value)} className="df-input-rect resize-none" /></Lbl>
              <Lbl label="Notes"><textarea rows="3" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} className="df-input-rect resize-none" /></Lbl>

              <div className="sm:col-span-2">
                <p className="text-white/85 mb-2 font-semibold text-sm">Service Options</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[["inbound_connect", "Inbound Connect"], ["tail_lift", "Tail Lift"], ["hand_unload", "Hand Unload"], ["dangerous_goods", "Dangerous Goods"]].map(([k, label]) => (
                    <Lbl key={k} label={label}>
                      <select value={data[k] || "No"} onChange={(e) => set(k, e.target.value)} className="df-input-rect">
                        {YN.map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
                      </select>
                    </Lbl>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 mt-6">
              <button onClick={cancelEdit} className="px-5 py-2.5 rounded-lg text-white font-semibold bg-white/10 hover:bg-white/15 transition-colors">Cancel</button>
              <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ background: "#22c55e" }} data-testid={`client-job-save-${job.id}`}>
                {busy ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Info label="Client Reference / Booking ID">{job.job_ref || "—"}</Info>
              {job.carrier_ref && <Info label="Carrier Reference" mono color="#a78bfa">{job.carrier_ref}</Info>}
              <Info label="Job Date">{formatDate(job.job_date) || "—"}</Info>
              <Info label="Freight Type">{job.freight_type || "—"}</Info>
              <div className="sm:col-span-2"><Info label="Pickup">{job.pickup_address || "—"}</Info></div>
              <div className="sm:col-span-2"><Info label="Drop-Off">{job.dropoff_address || "—"}</Info></div>
              <div className="sm:col-span-2">
                <p className="text-[11px] text-white/40 uppercase tracking-widest font-semibold mb-2">Service Options</p>
                <div className="flex flex-wrap gap-2">
                  <OptionPill label="Inbound Connect" value={job.inbound_connect} />
                  <OptionPill label="Tail Lift" value={job.tail_lift} />
                  <OptionPill label="Hand Unload" value={job.hand_unload} />
                  <OptionPill label="Dangerous Goods" value={job.dangerous_goods} />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Info label="Items">
                  {(job.item_qty || job.item_type) ? `${job.item_qty || 0} x ${job.item_type || "item"}` : "—"}
                  {(job.volume_cbm || job.weight_kgs) && (
                    <span className="text-white/45"> · {job.volume_cbm || 0} cbm · {job.weight_kgs || 0} kgs</span>
                  )}
                </Info>
              </div>
              {job.dimensions && <div className="sm:col-span-2"><Info label="Dimensions">{job.dimensions}</Info></div>}
              {job.notes && <div className="sm:col-span-2"><Info label="Additional Notes">{job.notes}</Info></div>}
            </div>

            {(job.quote_amount != null || (job.eta_date && job.eta_window)) && (
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-3">
                {job.quote_amount != null && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
                    <DollarSign size={14} />
                    <span>Estimate (incl. GST): ${Number(job.quote_amount).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                {job.eta_date && job.eta_window && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: "rgba(167,139,250,0.12)", color: "#a78bfa" }}>
                    <Clock size={14} />
                    <span>ETA: {formatDate(job.eta_date)} · {etaWindowLabel(job.eta_window)}</span>
                  </div>
                )}
              </div>
            )}

            <div className={`flex flex-wrap gap-2 ${(job.quote_amount != null || (job.eta_date && job.eta_window)) ? "mt-3" : "mt-6 pt-6 border-t border-white/10"}`}>
              <button onClick={() => downloadDoc(job.id, "cn", job.job_ref)}
                className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }} data-testid={`cn-${job.id}`}>
                <Download size={14} /> Consignment Note
              </button>
              {job.invoice_ref && (
                <span className="px-3 py-2 rounded-lg text-xs flex items-center gap-2 bg-white/[0.04] text-white/70" data-testid={`inv-ref-${job.id}`}>
                  <Receipt size={13} className="text-pink-400" />
                  <span className="font-mono text-white/85">{job.invoice_ref.invoice_number}</span>
                  <span className="font-mono text-emerald-300">${Number(job.invoice_ref.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="capitalize text-white/55">{(job.invoice_ref.invoice_status || "").replace(/_/g, " ")}</span>
                </span>
              )}
            </div>
            {job.invoice_ref && (
              <p className="text-[11px] text-white/40 mt-1.5">
                Preview and download invoices from the{" "}
                <Link to="/dashboard/invoicing" className="text-[#19e6c8] hover:underline">Invoicing tab</Link>.
              </p>
            )}

            {/* Remarks — visible when job is done. Always postable, even in Job
                History, regardless of whether the job's fields are still editable. */}
            {job.status === "done" && (
              <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/85 mb-1 font-semibold text-sm">Your Remarks</label>
                  <textarea rows="3"
                    value={remarksState[job.id]?.text ?? job.client_remarks ?? ""}
                    onChange={(e) => setRemarksState((s) => ({ ...s, [job.id]: { ...s[job.id], text: e.target.value } }))}
                    placeholder="Leave feedback or notes about this delivery..."
                    className="df-input-rect resize-none w-full"
                    data-testid={`client-remarks-${job.id}`} />
                  {job.client_remarks_updated_at && (
                    <p className="text-xs text-white/40 mt-1">
                      Updated: {formatDateTime(job.client_remarks_updated_at)}
                    </p>
                  )}
                  <button onClick={() => saveClientRemarks(job.id)} disabled={remarksState[job.id]?.saving}
                    className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}
                    data-testid={`save-remarks-${job.id}`}>
                    {remarksState[job.id]?.saving ? "Saving..." : "Save Remarks"}
                  </button>
                </div>
                {job.admin_remarks && (
                  <div>
                    <label className="block text-white/85 mb-1 font-semibold text-sm">Admin Remarks</label>
                    <textarea rows="3" readOnly value={job.admin_remarks} className="df-input-rect resize-none w-full opacity-70" />
                    {job.admin_remarks_updated_at && (
                      <p className="text-xs text-white/40 mt-1">
                        Updated: {formatDateTime(job.admin_remarks_updated_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Pickup & delivery photos — view/download only, no signatures
                (those are already captured on the consignment note). */}
            {(job.pod_image_urls?.length > 0 || job.onboard_image_urls?.length > 0) && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="brand-title text-lg mb-3">Pickup & Delivery Photos</h4>
                <PodGallery
                  imageUrls={job.pod_image_urls || []}
                  pickupImageUrls={job.onboard_image_urls || []}
                  podCompletedAt={job.pod_completed_at}
                />
              </div>
            )}

            {/* Documents — always uploadable/downloadable, in both Job Status and Job History. */}
            <ClientJobDocs job={job} onChanged={refresh} />
          </>
        )}
      </div>
    </div>
  );
}

const Lbl = ({ label, children }) => (
  <div>
    <label className="block text-white/85 mb-2 font-semibold text-sm">{label}</label>
    {children}
  </div>
);

// Read-only label/value block for the job card view (as opposed to Lbl,
// which wraps an editable input).
const Info = ({ label, children, mono, color }) => (
  <div>
    <p className="text-[11px] text-white/40 uppercase tracking-widest font-semibold mb-1.5">{label}</p>
    <div className={`text-sm ${mono ? "font-mono" : ""}`} style={{ color: color || "rgba(255,255,255,0.9)" }}>{children}</div>
  </div>
);

// Yes/No/N/A service-option badge — "Yes" gets the accent treatment so the
// handling requirements that actually apply stand out from the defaults.
const OptionPill = ({ label, value }) => (
  <span
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
    style={value === "Yes"
      ? { background: "rgba(25,230,200,0.15)", color: "#19e6c8" }
      : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" }}
  >
    {label}: {value || "N/A"}
  </span>
);

// Per-job documents — customer can upload categorised docs (Delivery Order,
// Outturn Report, etc.) and download/remove their own. Stored on the job's
// e_doc_files, so admin and the assigned driver see them in their portals.
function ClientJobDocs({ job, onChanged }) {
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const docs = job.e_doc_files || [];

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("doc_type", docType);
      await api.post(`/jobs/${job.id}/e-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (path) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/jobs/${job.id}/e-docs`, { params: { path } });
      toast.success("Document removed");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`client-docs-${job.id}`}>
      <p className="text-xs text-white/50 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <FileText size={12} /> Documents
      </p>
      <div className="space-y-2 mb-3">
        {docs.length === 0 && <p className="text-xs text-white/30 italic">No documents uploaded yet.</p>}
        {docs.map((d, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white/85 truncate">{d.name}</div>
              <div className="text-[10px] text-white/40">
                {d.doc_type || "Document"}{d.uploaded_by && d.uploaded_by !== "client" ? ` · added by ${d.uploaded_by}` : ""}
              </div>
            </div>
            <button onClick={() => downloadFile(d.path, d.name)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 shrink-0"
              style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
              <Download size={11} /> Download
            </button>
            {d.uploaded_by === "client" && (
              <button onClick={() => remove(d.path)}
                className="px-2 py-1.5 rounded-lg text-xs shrink-0"
                style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}
                title="Remove" data-testid={`client-doc-remove-${job.id}-${i}`}>
                <Trash2 size={11} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="df-input-rect !py-1.5 !text-sm !w-auto" data-testid={`client-doc-type-${job.id}`}>
          {DOC_TYPES.map((t) => <option key={t} value={t} className="bg-[#15151f]">{t}</option>)}
        </select>
        <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
          <Upload size={13} /> {busy ? "Uploading…" : "Upload Document"}
          <input type="file" multiple hidden disabled={busy}
            onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }}
            data-testid={`client-edoc-upload-${job.id}`} />
        </label>
      </div>
    </div>
  );
}

